// src/components/ConnectionTestPage.js
import React, { useState, useEffect } from 'react';
import { 
  Box, 
  Card, 
  CardContent, 
  Typography, 
  Button, 
  Alert, 
  CircularProgress,
  Grid,
  Chip,
  List,
  ListItem,
  ListItemText,
  ListItemIcon
} from '@mui/material';
import {
  CheckCircle,
  Error,
  Refresh,
  Wifi,
  Security,
  Api,
  Speed
} from '@mui/icons-material';
import { testConnection, getConnectionStatus } from '../utils/connectionTest';
import API_CONFIG from '../config/api';

const ConnectionTestPage = () => {
  const [testResults, setTestResults] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [connectionStatus, setConnectionStatus] = useState(null);

  useEffect(() => {
    // Get initial connection status
    setConnectionStatus(getConnectionStatus());
  }, []);

  const runConnectionTest = async () => {
    setIsLoading(true);
    try {
      const results = await testConnection();
      setTestResults(results);
      setConnectionStatus(getConnectionStatus());
    } catch (error) {
      console.error('Connection test failed:', error);
      setTestResults({
        error: error.message,
        timestamp: new Date().toISOString()
      });
    } finally {
      setIsLoading(false);
    }
  };

  const getStatusIcon = (success) => {
    return success ? (
      <CheckCircle color="success" />
    ) : (
      <Error color="error" />
    );
  };

  const getStatusChip = (success, label) => {
    return (
      <Chip
        icon={getStatusIcon(success)}
        label={label}
        color={success ? 'success' : 'error'}
        variant="outlined"
        size="small"
      />
    );
  };

  const renderTestResult = (testName, result) => {
    if (!result) return null;

    return (
      <Card key={testName} sx={{ mb: 2 }}>
        <CardContent>
          <Box display="flex" alignItems="center" justifyContent="space-between" mb={2}>
            <Typography variant="h6" component="h3">
              {testName.charAt(0).toUpperCase() + testName.slice(1)} Test
            </Typography>
            {getStatusChip(result.success, result.success ? 'PASSED' : 'FAILED')}
          </Box>

          {result.success ? (
            <Alert severity="success">
              Test completed successfully
              {result.status && ` (Status: ${result.status})`}
            </Alert>
          ) : (
            <Alert severity="error">
              <Typography variant="body2" component="div">
                <strong>Error:</strong> {result.error}
              </Typography>
              {result.details && (
                <Typography variant="body2" component="div" sx={{ mt: 1 }}>
                  <strong>Details:</strong> {JSON.stringify(result.details, null, 2)}
                </Typography>
              )}
            </Alert>
          )}
        </CardContent>
      </Card>
    );
  };

  const renderEndpointResults = (endpoints) => {
    if (!endpoints) return null;

    return (
      <Card sx={{ mb: 2 }}>
        <CardContent>
          <Typography variant="h6" component="h3" mb={2}>
            Authentication Endpoints
          </Typography>
          <List>
            {Object.entries(endpoints).map(([name, result]) => (
              <ListItem key={name}>
                <ListItemIcon>
                  {getStatusIcon(result.success)}
                </ListItemIcon>
                <ListItemText
                  primary={name.replace('_', ' ').toUpperCase()}
                  secondary={
                    result.success ? 
                      `Status: ${result.status || 'N/A'} - ${result.expected || 'Available'}` :
                      `Status: ${result.status || 'N/A'} - ${result.error}`
                  }
                />
              </ListItem>
            ))}
          </List>
        </CardContent>
      </Card>
    );
  };

  return (
    <Box sx={{ p: 3, maxWidth: 1200, mx: 'auto' }}>
            <Typography variant="h4" component="h1" gutterBottom>
        Connection Test Dashboard
      </Typography>
      
      <Typography variant="body1" color="text.secondary" mb={3}>
        Test the connection between frontend and backend API
      </Typography>

      {/* API Configuration Info */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Typography variant="h6" component="h2" gutterBottom>
            API Configuration
          </Typography>
          <Grid container spacing={2}>
            <Grid item xs={12} md={6}>
              <Typography variant="body2">
                <strong>Base URL:</strong> {API_CONFIG.BASE_URL}
              </Typography>
            </Grid>
            <Grid item xs={12} md={6}>
              <Typography variant="body2">
                <strong>Environment:</strong> {process.env.NODE_ENV}
              </Typography>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {/* Connection Status */}
      {connectionStatus && (
        <Card sx={{ mb: 3 }}>
          <CardContent>
            <Typography variant="h6" component="h2" gutterBottom>
              Connection Status
            </Typography>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6} md={3}>
                <Box display="flex" alignItems="center" gap={1}>
                  <Wifi color={connectionStatus.isConnected ? 'success' : 'error'} />
                  <Typography variant="body2">
                    API Connection: {connectionStatus.isConnected ? 'Connected' : 'Disconnected'}
            </Typography>
          </Box>
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <Box display="flex" alignItems="center" gap={1}>
                  <Security color={connectionStatus.hasCORS ? 'success' : 'error'} />
                  <Typography variant="body2">
                    CORS: {connectionStatus.hasCORS ? 'Configured' : 'Not Configured'}
                  </Typography>
        </Box>
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <Box display="flex" alignItems="center" gap={1}>
                  <Api color={connectionStatus.hasAuth ? 'success' : 'error'} />
                  <Typography variant="body2">
                    Auth: {connectionStatus.hasAuth ? 'Available' : 'Unavailable'}
                    </Typography>
                  </Box>
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <Box display="flex" alignItems="center" gap={1}>
                  <Speed color={connectionStatus.overall ? 'success' : 'error'} />
                  <Typography variant="body2">
                    Overall: {connectionStatus.overall ? 'Healthy' : 'Issues Detected'}
                  </Typography>
                </Box>
              </Grid>
            </Grid>
          </CardContent>
        </Card>
      )}

      {/* Test Button */}
      <Box display="flex" justifyContent="center" mb={3}>
                  <Button
                    variant="contained"
          size="large"
          onClick={runConnectionTest}
          disabled={isLoading}
          startIcon={isLoading ? <CircularProgress size={20} /> : <Refresh />}
        >
          {isLoading ? 'Running Tests...' : 'Run Connection Test'}
                  </Button>
                </Box>

        {/* Test Results */}
      {testResults && (
        <Box>
          {testResults.error ? (
            <Alert severity="error" sx={{ mb: 3 }}>
              <Typography variant="h6">Test Failed</Typography>
              <Typography variant="body2">{testResults.error}</Typography>
            </Alert>
          ) : (
            <>
              {/* Test Summary */}
              {testResults.summary && (
                <Card sx={{ mb: 3 }}>
                  <CardContent>
                    <Typography variant="h6" component="h2" gutterBottom>
                      Test Summary
                    </Typography>
                    <Grid container spacing={2}>
                      <Grid item xs={6} md={3}>
                        <Typography variant="body2">
                          <strong>Total Tests:</strong> {testResults.summary.total}
                        </Typography>
                      </Grid>
                      <Grid item xs={6} md={3}>
                        <Typography variant="body2">
                          <strong>Passed:</strong> {testResults.summary.passed}
                        </Typography>
                      </Grid>
                      <Grid item xs={6} md={3}>
                        <Typography variant="body2">
                          <strong>Failed:</strong> {testResults.summary.failed}
                        </Typography>
                      </Grid>
                      <Grid item xs={6} md={3}>
                        <Typography variant="body2">
                          <strong>Success Rate:</strong> {testResults.summary.successRate}%
            </Typography>
                      </Grid>
                    </Grid>
                    <Box mt={2}>
                      {getStatusChip(
                        testResults.summary.status === 'PASSED',
                        testResults.summary.status
                      )}
          </Box>
                  </CardContent>
                </Card>
              )}

              {/* Individual Test Results */}
              {testResults.tests && (
                <Box>
                  {renderTestResult('API Connection', testResults.tests.api)}
                  {renderTestResult('CORS Configuration', testResults.tests.cors)}
                  {renderEndpointResults(testResults.tests.auth)}
          </Box>
              )}

              {/* Timestamp */}
              <Typography variant="caption" color="text.secondary" display="block" textAlign="center">
                Last test run: {new Date(testResults.timestamp).toLocaleString()}
              </Typography>
            </>
          )}
        </Box>
      )}
    </Box>
  );
};

export default ConnectionTestPage;
